<template>
	<div class="col-12 p-fluid mb-4 spe-v2-filters">
		<div class="content-wrapper">
			<div class="content-header">Search</div>
			<div class="content-body">
				<div class="grid">
					<div class="col-12 md:col-6 lg:col-3 mb-4">
						<span class="p-float-label">
							<Dropdown
								id="spev2-filter-year"
								v-model="filters.year"
								:options="lovOptions.years"
								optionLabel="label"
								optionValue="value"
								showClear
								filter
								filterPlaceholder="Search year"
								:resetFilterOnHide="true"
								appendTo="body"
								panelClass="spe-v2-filter-lov-panel"
								:baseZIndex="SPE_V2_LOV_OVERLAY_Z_INDEX"
								:autoZIndex="true"
							/>
							<label for="spev2-filter-year">Year</label>
						</span>
					</div>

					<div class="col-12 md:col-6 lg:col-3 mb-4">
						<span class="p-float-label">
							<Dropdown
								id="spev2-filter-semester"
								v-model="filters.semester"
								:options="semesterOptions"
								optionLabel="label"
								optionValue="value"
								showClear
								filter
								filterPlaceholder="Search semester"
								:resetFilterOnHide="true"
								appendTo="body"
								panelClass="spe-v2-filter-lov-panel"
								:baseZIndex="SPE_V2_LOV_OVERLAY_Z_INDEX"
								:autoZIndex="true"
								:disabled="!filters.year || !semesterOptions.length"
							/>
							<label for="spev2-filter-semester">Semester</label>
						</span>
					</div>

					<div v-if="isUserPurchasing()" class="col-12 md:col-6 lg:col-3 mb-4">
						<span class="p-float-label">
							<Dropdown
								id="spev2-filter-supplier"
								v-model="filters.vendor_id"
								:options="lovOptions.suppliers"
								optionLabel="label"
								optionValue="value"
								filterBy="label"
								showClear
								filter
								filterPlaceholder="Search supplier name"
								:resetFilterOnHide="true"
								appendTo="body"
								panelClass="spe-v2-filter-lov-panel"
								:baseZIndex="SPE_V2_LOV_OVERLAY_Z_INDEX"
								:autoZIndex="true"
							/>
							<label for="spev2-filter-supplier">Supplier</label>
						</span>
					</div>

					<div class="col-12 md:col-6 lg:col-3 mb-4">
						<span class="p-float-label">
							<Dropdown
								id="spev2-filter-category"
								v-model="filters.category"
								:options="lovOptions.categories"
								optionLabel="label"
								optionValue="value"
								showClear
								filter
								filterPlaceholder="Search category"
								:resetFilterOnHide="true"
								appendTo="body"
								panelClass="spe-v2-filter-lov-panel"
								:baseZIndex="SPE_V2_LOV_OVERLAY_Z_INDEX"
								:autoZIndex="true"
							/>
							<label for="spev2-filter-category">Category</label>
						</span>
					</div>

					<div class="col-12 md:col-6 lg:col-3 mb-4">
						<span class="p-float-label">
							<Dropdown
								id="spev2-filter-method"
								v-model="filters.method"
								:options="lovOptions.methods"
								optionLabel="label"
								optionValue="value"
								showClear
								filter
								filterPlaceholder="Search method"
								:resetFilterOnHide="true"
								appendTo="body"
								panelClass="spe-v2-filter-lov-panel"
								:baseZIndex="SPE_V2_LOV_OVERLAY_Z_INDEX"
								:autoZIndex="true"
							/>
							<label for="spev2-filter-method">Method</label>
						</span>
					</div>

					<div class="col-12 md:col-6 lg:col-3 mb-4">
						<span class="p-float-label">
							<Dropdown
								id="spev2-filter-score"
								v-model="filters.score_grade"
								:options="lovOptions.scoreGrades"
								optionLabel="label"
								optionValue="value"
								showClear
								filter
								filterPlaceholder="Search score"
								:resetFilterOnHide="true"
								appendTo="body"
								panelClass="spe-v2-filter-lov-panel"
								:baseZIndex="SPE_V2_LOV_OVERLAY_Z_INDEX"
								:autoZIndex="true"
							/>
							<label for="spev2-filter-score">Score</label>
						</span>
					</div>

					<div class="col-12 lg:col-4">
						<Button class="p-button-success w-auto mr-2" @click="onSearch">
							<span class="font-bold uppercase">Search</span>
						</Button>
						<Button class="w-auto p-button-secondary" @click="onClear">
							<span class="font-bold uppercase">Clear</span>
						</Button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, watch, computed, defineProps, defineEmits } from 'vue';
import SpeV2Service from '../../service/SpeV2Service';

/** Keep filter LOV panels above sticky/frozen Search Results table. */
const SPE_V2_LOV_OVERLAY_Z_INDEX = 1100;

const props = defineProps({
	currentUser: { type: Object, default: null },
	company: { type: Object, default: null },
	vendorId: { type: [String, Number], default: null },
});

const emit = defineEmits(['search', 'clear']);

const defaultScoreGrades = () => [
	{ label: 'EXCELLENT', value: 'EXCELLENT' },
	{ label: 'GOOD', value: 'GOOD' },
	{ label: 'AVERAGE', value: 'AVERAGE' },
	{ label: 'BAD', value: 'BAD' },
	{ label: 'POOR', value: 'POOR' },
];

const defaultFilters = () => ({
	year: null,
	semester: null,
	category: null,
	method: null,
	score_grade: null,
	vendor_id: null,
});

const filters = ref(defaultFilters());
const lovOptions = ref({
	years: [],
	semesters: [],
	periods: [],
	categories: [],
	methods: [],
	suppliers: [],
	scoreGrades: defaultScoreGrades(),
});

const toOption = (value) => ({
	label: String(value),
	value,
});

const semesterOptions = computed(() => {
	const periods = lovOptions.value.periods || [];
	if (!periods.length) {
		return lovOptions.value.semesters || [];
	}

	const selectedYear = filters.value.year != null ? Number(filters.value.year) : null;
	const filtered = Number.isFinite(selectedYear)
		? periods.filter((period) => Number(period.year) === selectedYear)
		: periods;

	const unique = [];
	const seen = new Set();
	filtered.forEach((period) => {
		const semester = Number(period.semester ?? period.value);
		if (!seen.has(semester) && (semester === 1 || semester === 2)) {
			seen.add(semester);
			unique.push({
				label: period.label || `S${semester}`,
				value: semester,
			});
		}
	});

	return unique.sort((a, b) => a.value - b.value);
});

const isUserPurchasing = () => {
	const leveltenant = props.currentUser && props.currentUser.leveltenant;
	return leveltenant == 1 || leveltenant == 3;
};

const isUserSupplier = () => props.currentUser && props.currentUser.leveltenant == 2;

const canLoadLov = () => {
	if (!props.company || !props.currentUser) return false;
	if (isUserSupplier() && !props.vendorId) return false;
	return true;
};

const loadLov = async () => {
	if (!canLoadLov()) return;

	try {
		const params = {
			org_id: props.company.value,
			db_instance: props.company.db_instance,
			leveltenant: props.currentUser.leveltenant,
		};

		if (isUserSupplier() && props.vendorId) {
			params.vendor_id = props.vendorId;
		}

		const res = await SpeV2Service.getFilters(params);
		if (res.status === 200 && res.data.data) {
			const data = res.data.data;
			lovOptions.value = {
				years: (data.years || []).map(toOption),
				semesters: data.semesters || [],
				periods: data.periods || [],
				categories: (data.categories || []).map(toOption),
				methods: (data.methods || []).map(toOption),
				suppliers: isUserPurchasing()
					? (data.suppliers || [])
						.filter((supplier) => supplier.label && String(supplier.label).trim())
						.map((supplier) => ({
							label: String(supplier.label).trim(),
							value: String(supplier.value),
						}))
					: [],
				scoreGrades: data.scoreGrades || defaultScoreGrades(),
			};
		}
	} catch (err) {
		console.log(err);
	}
};

const onSearch = () => {
	emit('search', { ...filters.value });
};

const onClear = () => {
	filters.value = defaultFilters();
	emit('clear');
};

watch(
	() => filters.value.year,
	() => {
		const allowed = new Set(semesterOptions.value.map((item) => Number(item.value)));
		if (filters.value.semester != null && !allowed.has(Number(filters.value.semester))) {
			filters.value.semester = null;
		}
	}
);

watch(
	() => [props.company, props.vendorId],
	() => {
		filters.value = defaultFilters();
		if (canLoadLov()) {
			loadLov();
		}
	},
	{ immediate: true }
);
</script>

<style scoped>
.spe-v2-filters {
	position: relative;
	z-index: 5;
}
</style>

<style>
/* Body-appended filter LOVs must sit above sticky/frozen Search Results */
.spe-v2-filter-lov-panel.p-dropdown-panel {
	z-index: 1100 !important;
}
</style>
